import type { ReactElement, ReactNode } from 'react'

import { cloneElement, isValidElement } from 'react'

import type { TextFieldProps } from './textField.types'

import Label from '../label'
import { isString } from '../utils'
import { helpTextMargin } from './consts'

/** Returns length of the initial field's value. */
export function getInitialCount(props: TextFieldProps) {
  const { defaultValue, value } = props

  return defaultValue?.toString().length ?? value?.toString().length ?? 0
}

/** Reserves horizontal space for the counter so help/error text doesn't run underneath it. */
export function getHelpTextMargin(showCount: boolean | undefined) {
  return showCount ? helpTextMargin : undefined
}

/** Truncates a value to `maxLength`, if set. */
export function clampValue(value: string, maxLength?: number) {
  return maxLength ? value.slice(0, maxLength) : value
}

/** Builds a minimal synthetic change event carrying just the given value. */
export function createSyntheticChangeEvent(value: string, type: string) {
  return { target: { value }, type }
}

/** Predicate for filtering autocomplete options against the field's current value. */
export function filterAutoCompleteOption(currentValue: string, option: string) {
  return currentValue === option ? false : !currentValue || option.toLowerCase().includes(currentValue.toLowerCase())
}

/** Matches `Label` elements, including thin wrappers (styled/memo) that preserve the `Label` display name. */
function isLabelElement(label: ReactNode): label is ReactElement<{ htmlFor?: string }> {
  if (!isValidElement<{ htmlFor?: string }>(label)) return false

  const type = label.type as { displayName?: string }

  return label.type === Label || type?.displayName === Label.displayName
}

/** Resolves the `label` prop: wraps strings in `Label`, auto-wires `htmlFor` onto `Label` elements. */
export function resolveLabel(
  label: ReactNode,
  id: string,
  disabled: boolean | undefined,
  labelMarkOptional: boolean | undefined,
  labelTooltipText: ReactNode,
  labelStyles: Record<string, unknown>,
) {
  if (isString(label)) {
    return (
      <Label
        aria-disabled={disabled}
        className="vui-textFieldLabel"
        htmlFor={id}
        markAsOptional={labelMarkOptional}
        text={label}
        tooltipText={labelTooltipText}
        {...labelStyles}
      />
    )
  }

  if (isLabelElement(label)) {
    return cloneElement(label, { htmlFor: label.props.htmlFor ?? id })
  }

  return label
}
